/**
 * A QR encoder, plain JavaScript, no dependency.  Telethon on the TV (2026-10-31).
 *
 * The member card's encoder (apps/mobile/src/lib/qr.ts, CA-74/CA-89) is TypeScript inside the Expo app, so neither the
 * TV page (prototype/tv.html, served as a plain file) nor the staff panel can load it. This is the same encoder, in the
 * form both can use: CommonJS for core's tests and the panel (import from core), a browser global `MasjidQR` for the
 * TV (served at /tv/assets/qr.js by the API — never from a CDN; the wall's policy allows no other origin).
 *
 * WHAT IT ENCODES: a campaign page's address — `https://<mosque>.masjid.no/v1/gi/<slug>` — drawn on the wall under
 * «Skann for å gi» and downloaded as a PNG from «Innsamlinger». That is up to about a hundred bytes, so this goes to
 * version 6 (108 bytes at level M); the card's stops at 4. Versions 7 and up carry a version-information block this
 * file does not write, so a longer payload is REFUSED (an error), never truncated: a QR that scans to a wrong address
 * is worse than none, and the painter draws nothing for a payload it cannot encode.
 *
 * Byte mode, error-correction level M (~15 % recovery: a phone held at arm's length across a hall), the mask chosen
 * by the specification's penalty rules. Every bug the card's encoder shipped and fixed (format bits LSB-first and
 * transposed, the generator polynomial's two multiplications) is kept fixed here — the structure is identical, and
 * packages/core/test/qr.test.js asserts the same things the app's test does.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.MasjidQR = factory();
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /** version, size, data bytes at level M, EC codewords per block, blocks (all blocks equal through version 6), alignment centres. */
  const VERSIONS = [
    { v: 1, size: 21, data: 16, ec: 10, blocks: 1, align: [] },
    { v: 2, size: 25, data: 28, ec: 16, blocks: 1, align: [6, 18] },
    { v: 3, size: 29, data: 44, ec: 26, blocks: 1, align: [6, 22] },
    { v: 4, size: 33, data: 64, ec: 18, blocks: 2, align: [6, 26] },
    { v: 5, size: 37, data: 86, ec: 24, blocks: 2, align: [6, 30] },
    { v: 6, size: 41, data: 108, ec: 16, blocks: 4, align: [6, 34] },
  ];
  /**
   * What a version HOLDS in payload bytes: its data codewords less the twelve header bits (mode + count), which round
   * up to two bytes. Choosing a version by data codewords alone (the card's encoder did, 2026-10-31) lets a payload of
   * exactly 15–16, 27–28, 43–44 or 63–64 bytes overflow its version: the codewords past the block are silently
   * dropped, the error correction is computed over the rest, and the code looks perfect and decodes as nothing.
   */
  const holds = (v) => v.data - 2;
  const MAX_BYTES = holds(VERSIONS[VERSIONS.length - 1]);

  /* ---------------- GF(256) arithmetic for Reed–Solomon ---------------- */
  const EXP = new Uint8Array(512);
  const LOG = new Uint8Array(256);
  (function () {
    let x = 1;
    for (let i = 0; i < 255; i++) {
      EXP[i] = x; LOG[x] = i;
      x <<= 1;
      if (x & 0x100) x ^= 0x11d;            // the QR primitive polynomial
    }
    for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255];
  }());
  const mul = (a, b) => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

  /** The generator polynomial, coefficients DESCENDING (see qr.ts: which line multiplies by x is the whole thing). */
  function generator(degree) {
    let poly = [1];
    for (let i = 0; i < degree; i++) {
      const next = new Array(poly.length + 1).fill(0);
      for (let j = 0; j < poly.length; j++) {
        next[j] ^= poly[j];                        // × x
        next[j + 1] ^= mul(poly[j], EXP[i]);       // × α^i
      }
      poly = next;
    }
    return poly;
  }

  function ecFor(data, ecLen) {
    const gen = generator(ecLen);
    const rem = new Array(ecLen).fill(0);
    for (const byte of data) {
      const factor = byte ^ rem[0];
      rem.shift(); rem.push(0);
      if (factor !== 0) for (let i = 0; i < ecLen; i++) rem[i] ^= mul(gen[i + 1], factor);
    }
    return rem;
  }

  /* ---------------- the matrix ---------------- */
  const blank = (size) => Array.from({ length: size }, () => new Array(size).fill(null));

  function placeFinder(m, row, col) {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const rr = row + r, cc = col + c;
        if (rr < 0 || cc < 0 || rr >= m.length || cc >= m.length) continue;
        const on = (r >= 0 && r <= 6 && (c === 0 || c === 6)) || (c >= 0 && c <= 6 && (r === 0 || r === 6)) || (r >= 2 && r <= 4 && c >= 2 && c <= 4);
        m[rr][cc] = on ? 1 : 0;
      }
    }
  }

  function placeAlignment(m, centres) {
    for (const r of centres) {
      for (const c of centres) {
        // The three finder corners already own these positions.
        if ((r <= 8 && c <= 8) || (r <= 8 && c >= m.length - 9) || (r >= m.length - 9 && c <= 8)) continue;
        for (let dr = -2; dr <= 2; dr++) for (let dc = -2; dc <= 2; dc++) m[r + dr][c + dc] = Math.max(Math.abs(dr), Math.abs(dc)) !== 1 ? 1 : 0;
      }
    }
  }

  /** 15 bits: two ECC-level bits (00 = M), three mask bits, ten BCH, XOR 0x5412 — returned LEAST-significant bit first. */
  function formatBits(mask) {
    const data = (0b00 << 3) | mask;
    let rem = data;
    for (let i = 0; i < 10; i++) { rem <<= 1; if (rem & 0x400) rem ^= 0x537; }
    const value = (((data << 10) | rem) ^ 0x5412) & 0x7fff;
    const out = [];
    for (let i = 0; i <= 14; i++) out.push((value >>> i) & 1);
    return out;
  }

  const MASKS = [
    (r, c) => (r + c) % 2 === 0,
    (r) => r % 2 === 0,
    (r, c) => c % 3 === 0,
    (r, c) => (r + c) % 3 === 0,
    (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
    (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
    (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
    (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
  ];

  /** The four penalty rules; lower is better. */
  function penalty(m) {
    const n = m.length;
    let score = 0;
    const runScore = (line) => {
      let s = 0, run = 1;
      for (let i = 1; i < line.length; i++) {
        if (line[i] === line[i - 1]) { run++; if (run === 5) s += 3; else if (run > 5) s += 1; } else run = 1;
      }
      return s;
    };
    for (let i = 0; i < n; i++) { score += runScore(m[i]); score += runScore(m.map((row) => row[i])); }
    for (let r = 0; r < n - 1; r++) for (let c = 0; c < n - 1; c++) {
      if (m[r][c] === m[r][c + 1] && m[r][c] === m[r + 1][c] && m[r][c] === m[r + 1][c + 1]) score += 3;
    }
    const pattern = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0], reversed = pattern.slice().reverse();
    const hasAt = (line, i) => pattern.every((p, k) => line[i + k] === p);
    const hasRevAt = (line, i) => reversed.every((p, k) => line[i + k] === p);
    for (let i = 0; i < n; i++) {
      const row = m[i], col = m.map((x) => x[i]);
      for (let j = 0; j + 11 <= n; j++) {
        if (hasAt(row, j) || hasRevAt(row, j)) score += 40;
        if (hasAt(col, j) || hasRevAt(col, j)) score += 40;
      }
    }
    let dark = 0;
    for (const row of m) for (const cell of row) dark += cell;
    score += Math.floor(Math.abs((dark * 100) / (n * n) - 50) / 5) * 10;
    return score;
  }

  function writeFormat(m, mask, size) {
    const f = formatBits(mask);
    // Copy 1: bits 0–5 down column 8; bit 6 at (7,8); bit 7 at (8,8); bit 8 at (8,7); bits 9–14 leftwards along row 8.
    for (let i = 0; i <= 5; i++) m[i][8] = f[i];
    m[7][8] = f[6]; m[8][8] = f[7]; m[8][7] = f[8];
    for (let i = 9; i <= 14; i++) m[8][14 - i] = f[i];
    // Copy 2: bits 0–7 along row 8 from the right edge inwards; bits 8–14 up column 8 from the bottom edge.
    for (let i = 0; i <= 7; i++) m[8][size - 1 - i] = f[i];
    for (let i = 8; i <= 14; i++) m[size - 15 + i][8] = f[i];
    m[size - 8][8] = 1;                   // the dark module, always, and last
  }

  /** UTF-8 bytes of a string, with or without TextEncoder (an older TV browser). */
  function utf8(text) {
    if (typeof TextEncoder !== 'undefined') return Array.from(new TextEncoder().encode(text));
    const out = [];
    for (const ch of String(text)) {
      let cp = ch.codePointAt(0);
      if (cp < 0x80) out.push(cp);
      else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 63));
      else if (cp < 0x10000) out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
      else out.push(0xf0 | (cp >> 18), 0x80 | ((cp >> 12) & 63), 0x80 | ((cp >> 6) & 63), 0x80 | (cp & 63));
    }
    return out;
  }

  /**
   * Encode `text` as a matrix of booleans (true = dark), versions 1–6, level M. Throws for a payload over MAX_BYTES
   * rather than truncating. `forceMask` (0–7) is for tests: every mask must produce a valid code.
   */
  function encode(text, forceMask) {
    const bytes = utf8(text);
    const spec = VERSIONS.find((v) => bytes.length <= holds(v));
    if (!spec) throw new Error(`QR payload too long: ${bytes.length} bytes, max ${MAX_BYTES}`);

    // --- data codewords: mode, count (8 bits for versions 1–9), the bytes, terminator, pad ---
    const bits = [];
    const push = (value, length) => { for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1); };
    push(0b0100, 4);
    push(bytes.length, 8);
    for (const b of bytes) push(b, 8);
    push(0, Math.min(4, spec.data * 8 - bits.length));
    while (bits.length % 8) bits.push(0);
    const codewords = [];
    for (let i = 0; i < bits.length; i += 8) {
      let byte = 0;
      for (let j = 0; j < 8; j++) byte = (byte << 1) | bits[i + j];
      codewords.push(byte);
    }
    const PAD = [0xec, 0x11];
    for (let i = 0; codewords.length < spec.data; i++) codewords.push(PAD[i % 2]);

    // --- blocks, error correction, interleaving ---
    const perBlock = spec.data / spec.blocks;
    const dataBlocks = [], ecBlocks = [];
    for (let b = 0; b < spec.blocks; b++) {
      const block = codewords.slice(b * perBlock, (b + 1) * perBlock);
      dataBlocks.push(block); ecBlocks.push(ecFor(block, spec.ec));
    }
    const stream = [];
    for (let i = 0; i < perBlock; i++) for (const b of dataBlocks) stream.push(b[i]);
    for (let i = 0; i < spec.ec; i++) for (const b of ecBlocks) stream.push(b[i]);

    // --- function patterns ---
    const size = spec.size;
    const m = blank(size);
    placeFinder(m, 0, 0); placeFinder(m, 0, size - 7); placeFinder(m, size - 7, 0);
    placeAlignment(m, spec.align);
    for (let i = 8; i < size - 8; i++) { m[6][i] = i % 2 === 0 ? 1 : 0; m[i][6] = i % 2 === 0 ? 1 : 0; }
    m[size - 8][8] = 1;
    for (let i = 0; i <= 8; i++) { if (m[8][i] === null) m[8][i] = 0; if (m[i][8] === null) m[i][8] = 0; }
    for (let i = 0; i < 8; i++) { if (m[8][size - 1 - i] === null) m[8][size - 1 - i] = 0; if (m[size - 1 - i][8] === null) m[size - 1 - i][8] = 0; }

    // --- data placement, two columns at a time, upward then downward; the timing column is skipped ---
    const reserved = m.map((row) => row.map((c) => c !== null));
    const streamBits = [];
    for (const byte of stream) for (let i = 7; i >= 0; i--) streamBits.push((byte >> i) & 1);
    let bit = 0, upward = true;
    for (let col = size - 1; col > 0; col -= 2) {
      if (col === 6) col--;
      for (let i = 0; i < size; i++) {
        const row = upward ? size - 1 - i : i;
        for (const c of [col, col - 1]) {
          if (reserved[row][c]) continue;
          const v = streamBits[bit++];
          m[row][c] = v === undefined ? 0 : v;   // the remainder bits (versions 2–6) are zero
        }
      }
      upward = !upward;
    }

    // --- the mask by penalty, then the format bits ---
    let best = null, bestScore = Infinity;
    for (let mask = 0; mask < 8; mask++) {
      if (forceMask !== undefined && mask !== forceMask) continue;
      const cand = m.map((row, r) => row.map((cell, c) => (reserved[r][c] ? cell : cell ^ (MASKS[mask](r, c) ? 1 : 0))));
      writeFormat(cand, mask, size);
      const s = penalty(cand);
      if (s < bestScore) { bestScore = s; best = cand; }
    }
    return best.map((row) => row.map((c) => c === 1));
  }

  /**
   * Draw a matrix on a canvas-like 2D context: a white plate with `quiet` modules of clearance (four is the
   * specification's), dark modules in black, each a hair over one cell so neighbours meet. Returns the size drawn.
   * The TV and the panel both draw this way; a PNG for print is the panel's canvas at `scale` pixels per module.
   */
  function draw(ctx, matrix, { scale = 8, quiet = 4 } = {}) {
    const n = matrix.length + quiet * 2;
    const px = n * scale;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, px, px);
    ctx.fillStyle = '#000000';
    matrix.forEach((row, r) => row.forEach((on, c) => { if (on) ctx.fillRect((c + quiet) * scale, (r + quiet) * scale, scale + 0.5, scale + 0.5); }));
    return px;
  }

  /** How many pixels a canvas needs for `matrix` at `scale` pixels per module with `quiet` modules of clearance. */
  const pixels = (matrix, { scale = 8, quiet = 4 } = {}) => (matrix.length + quiet * 2) * scale;

  return { encode, draw, pixels, MAX_BYTES, VERSIONS };
}));
